import { useState } from "react";
import { Images, Utensils, X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Photo gallery + fullscreen lightbox for the public venue profile.
 *
 * Lifted out of `VenueDetailPage.jsx` (was lines 309-403). All click handlers
 * for opening / navigating / closing the lightbox now live here so the parent
 * doesn't have to track `currentImageIndex` or `lightboxOpen`.
 *
 * @param {string[]} images   absolute (already-resolved) image URLs
 * @param {string}   alt      base alt text (typically the venue name)
 * @param {string}   testId   data-testid override (defaults to venue-gallery)
 */
export default function VenueHero({ images, alt, testId = "venue-gallery" }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const open = (i) => {
    setCurrentIndex(i);
    setLightboxOpen(true);
  };

  return (
    <>
      <section className="relative bg-muted" data-testid={testId}>
        {images.length > 0 ? (
          <div className="max-w-[1400px] mx-auto">
            {images.length === 1 ? (
              <div
                className="h-[50vh] min-h-[400px] cursor-pointer"
                onClick={() => open(0)}
              >
                <img src={images[0]} alt={alt} className="w-full h-full object-cover" />
              </div>
            ) : images.length === 2 ? (
              <div className="grid grid-cols-2 gap-1 h-[50vh] min-h-[400px]">
                {images.slice(0, 2).map((img, i) => (
                  <div key={i} className="cursor-pointer overflow-hidden" onClick={() => open(i)}>
                    <img
                      src={img}
                      alt={`${alt} ${i + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-4 grid-rows-2 gap-1 h-[50vh] min-h-[400px]">
                <div
                  className="col-span-2 row-span-2 cursor-pointer overflow-hidden"
                  onClick={() => open(0)}
                >
                  <img
                    src={images[0]}
                    alt={alt}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                {images.slice(1, 5).map((img, i) => (
                  <div
                    key={i}
                    className="cursor-pointer overflow-hidden"
                    onClick={() => open(i + 1)}
                  >
                    <img
                      src={img}
                      alt={`${alt} ${i + 2}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            )}
            {images.length > 1 && (
              <button
                onClick={() => open(0)}
                className="absolute bottom-4 right-4 bg-white text-foreground px-4 py-2 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-50 transition-colors"
                data-testid="view-all-photos"
              >
                <Images className="h-4 w-4" />
                View all {images.length} photos
              </button>
            )}
          </div>
        ) : (
          <div className="h-[50vh] min-h-[400px] flex items-center justify-center">
            <Utensils className="h-24 w-24 text-muted-foreground" />
          </div>
        )}
      </section>

      {lightboxOpen && images.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="photo-lightbox">
          <div className="flex items-center justify-between p-4">
            <span className="text-white text-sm">
              {currentIndex + 1} / {images.length}
            </span>
            <button
              onClick={() => setLightboxOpen(false)}
              className="text-white p-2 hover:bg-white/10 rounded-full"
              data-testid="lightbox-close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center px-16 relative">
            <img
              src={images[currentIndex]}
              alt={`${alt} ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={() =>
                    setCurrentIndex((i) => (i - 1 + images.length) % images.length)
                  }
                  className="absolute left-4 p-3 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={() => setCurrentIndex((i) => (i + 1) % images.length)}
                  className="absolute right-4 p-3 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors"
                  data-testid="lightbox-next"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </div>
          <div className="flex justify-center gap-2 p-4 overflow-x-auto">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-16 h-16 rounded-lg overflow-hidden shrink-0 border-2 transition-colors ${
                  i === currentIndex
                    ? "border-white"
                    : "border-transparent opacity-60 hover:opacity-100"
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
